import {
  CheckCircleOutlined,
  StopOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { Alert, Tag } from "antd";
import { useTranslation } from "react-i18next";
import { MdOpenInNew } from "react-icons/md";
import Button from "shared/components/Button";
import Spin from "shared/components/Spin";
import { NAME_SPACES } from "shared/locales/constants";
import { COLORS } from "shared/style/colors";
import { PPE_PERSON_STATUS } from "utils/constants";
import { usePPEGEL } from "./PPEGELProvider";
import { computePersonStatus } from "./utils";

const statusIconMap = {
  [PPE_PERSON_STATUS.VERIFIED]: <CheckCircleOutlined />,
  [PPE_PERSON_STATUS.ALERT]: <WarningOutlined />,
  [PPE_PERSON_STATUS.BLOCKED]: <StopOutlined />,
  [PPE_PERSON_STATUS.PENDING]: null,
};

const statusColorMap = {
  [PPE_PERSON_STATUS.VERIFIED]: "success",
  [PPE_PERSON_STATUS.ALERT]: "error",
  [PPE_PERSON_STATUS.BLOCKED]: "error",
  [PPE_PERSON_STATUS.PENDING]: "default",
};

const PersonRow = ({ person, isBlocked, t }) => {
  const personStatus = isBlocked
    ? PPE_PERSON_STATUS.BLOCKED
    : computePersonStatus(person.alerts);

  return (
    <div className="ppegel-section__person-row">
      <div className="ppegel-section__person-info">
        <span className="ppegel-section__person-name">{person.fullName}</span>
        <span className="ppegel-section__person-role">
          {t(`ROLES.${person.role}`) || person.role}
        </span>
      </div>
      <Tag
        color={statusColorMap[personStatus] || "default"}
        icon={statusIconMap[personStatus]}
        className="ppegel-section__status-tag"
      >
        {t(`PERSON_STATUSES.${personStatus}`)}
      </Tag>
    </div>
  );
};

const PPEGELControlSection = () => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.PPEGEL);
  const { persons, isBlocked, blockedByName, loading, openDrawer } =
    usePPEGEL();

  if (loading) {
    return (
      <div className="ppegel-section">
        <div className="ppegel-section__header">
          <h3 className="ppegel-section__title">{t("TITLE")}</h3>
        </div>
        <div className="ppegel-section__loading">
          <Spin />
        </div>
      </div>
    );
  }

  return (
    <div className="ppegel-section">
      <div className="ppegel-section__header">
        <h3 className="ppegel-section__title">{t("TITLE")}</h3>
        <Button type="primary--link" onClick={openDrawer}>
          <MdOpenInNew color={COLORS.C_PRIMARY} size={18} />
        </Button>
      </div>

      {isBlocked && (
        <Alert
          type="error"
          showIcon
          className="ppegel-section__blocked-banner"
          message={t("SECTION.BLOCKED_BANNER", { name: blockedByName })}
        />
      )}

      <div className="ppegel-section__persons">
        {persons.map((person, index) => (
          <PersonRow
            key={person.personId || index}
            person={person}
            isBlocked={isBlocked}
            t={t}
          />
        ))}
      </div>
    </div>
  );
};

export default PPEGELControlSection;
